/**
 * ShortcutsOverlay
 *
 * Modal triggered by Cmd+/ that displays all keyboard shortcuts grouped by scope.
 * Searchable, closes on Esc or Cmd+/ again.
 *
 * Accessibility:
 *   - Screen-reader friendly with proper headings per scope
 *   - Focus trapped within modal
 *   - Closes on Escape
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Search, X } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Kbd } from '@/shared/components/ui/kbd'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { useShortcutStore } from '@/shared/stores/shortcut-store'
import { useShortcut } from '@/shared/hooks/useShortcut'
import {
  SHORTCUT_GROUPS,
  formatShortcut,
  getShortcutById,
  type ShortcutDefinition,
  type ShortcutGroup,
} from '@/shared/lib/shortcuts'

export function ShortcutsOverlay() {
  const open = useShortcutStore((state) => state.overlayOpen)
  const setOverlayOpen = useShortcutStore((state) => state.setOverlayOpen)
  const toggleOverlay = useShortcutStore((state) => state.toggleOverlay)

  const [search, setSearch] = React.useState('')
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // Register the Cmd+/ shortcut to toggle the overlay
  useShortcut('shortcuts-overlay', () => {
    toggleOverlay()
  })

  // Register bare ? shortcut to toggle the overlay
  useShortcut('shortcuts-overlay-bare', () => {
    toggleOverlay()
  })

  // Reset search when overlay closes
  React.useEffect(() => {
    if (!open) {
      setSearch('')
    }
  }, [open])

  // Focus search input when overlay opens
  React.useEffect(() => {
    if (open) {
      // Small delay to ensure dialog is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  // Filter shortcuts based on search
  const filteredGroups = React.useMemo(() => {
    if (!search.trim()) return SHORTCUT_GROUPS

    const query = search.toLowerCase()
    return SHORTCUT_GROUPS.map((group) => ({
      ...group,
      shortcuts: group.shortcuts.filter(
        (shortcut) =>
          shortcut.description.toLowerCase().includes(query) ||
          shortcut.key.toLowerCase().includes(query) ||
          formatShortcut(shortcut).toLowerCase().includes(query),
      ),
    })).filter((group) => group.shortcuts.length > 0)
  }, [search])

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      // Close on Escape
      if (e.key === 'Escape') {
        e.preventDefault()
        setOverlayOpen(false)
      }
      // Also close on Cmd+/
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault()
        setOverlayOpen(false)
      }
    },
    [setOverlayOpen],
  )

  const shortcutDef = getShortcutById('shortcuts-overlay')

  return (
    <Dialog open={open} onOpenChange={setOverlayOpen}>
      <DialogContent
        style={styles.content}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
        </DialogHeader>

        {/* Search input */}
        <div style={styles.searchWrapper}>
          <Search style={styles.searchIcon} />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Search shortcuts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
            aria-label="Search shortcuts"
          />
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              style={styles.clearButton}
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <X style={{ width: '12px', height: '12px' }} />
            </Button>
          )}
        </div>

        {/* Shortcut groups */}
        <div style={styles.groupsContainer}>
          {filteredGroups.length === 0 ? (
            <p style={styles.emptyState}>
              No shortcuts found for &quot;{search}&quot;
            </p>
          ) : (
            <div style={styles.groupsList}>
              {filteredGroups.map((group) => (
                <ShortcutGroupSection key={group.scope} group={group} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <span>
            Press <Kbd>Esc</Kbd> or{' '}
            {shortcutDef && <Kbd>{formatShortcut(shortcutDef)}</Kbd>} to close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface ShortcutGroupSectionProps {
  group: ShortcutGroup
}

function ShortcutGroupSection({ group }: ShortcutGroupSectionProps) {
  return (
    <section aria-labelledby={`shortcuts-${group.scope}`}>
      <h3 id={`shortcuts-${group.scope}`} style={sectionStyles.heading}>
        {group.label}
      </h3>
      <ul style={sectionStyles.list} role="list">
        {group.shortcuts.map((shortcut) => (
          <ShortcutRow key={shortcut.id} shortcut={shortcut} />
        ))}
      </ul>
    </section>
  )
}

interface ShortcutRowProps {
  shortcut: ShortcutDefinition
}

function ShortcutRow({ shortcut }: ShortcutRowProps) {
  const formatted = formatShortcut(shortcut)

  return (
    <li style={sectionStyles.row} className="shortcut-row">
      <span style={sectionStyles.description}>{shortcut.description}</span>
      <ShortcutKeys keys={formatted} />
    </li>
  )
}

interface ShortcutKeysProps {
  keys: string
}

function ShortcutKeys({ keys }: ShortcutKeysProps) {
  // Split by + to render each key separately
  const parts = keys.split('+')

  return (
    <span style={sectionStyles.keysWrapper}>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          <Kbd>{part}</Kbd>
          {index < parts.length - 1 && (
            <span style={sectionStyles.plus}>+</span>
          )}
        </React.Fragment>
      ))}
    </span>
  )
}

export default ShortcutsOverlay

const styles: Record<string, CSSProperties> = {
  content: {
    maxHeight: '80vh',
    maxWidth: '576px',
    overflow: 'hidden',
  },
  searchWrapper: {
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '16px',
    height: '16px',
    color: 'var(--text-secondary)',
  },
  searchInput: {
    paddingLeft: '40px',
    paddingRight: '40px',
  },
  clearButton: {
    position: 'absolute',
    right: '4px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '28px',
    height: '28px',
  },
  groupsContainer: {
    marginTop: '16px',
    maxHeight: '50vh',
    overflow: 'auto',
  },
  emptyState: {
    padding: '32px 0',
    textAlign: 'center',
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  groupsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '16px',
    borderTop: '1px solid var(--border-default)',
    paddingTop: '16px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
}

const sectionStyles: Record<string, CSSProperties> = {
  heading: {
    marginBottom: '12px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 'var(--radius-sm)',
    padding: '6px 8px',
  },
  description: {
    fontSize: '14px',
    color: 'var(--text-primary)',
  },
  keysWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  plus: {
    color: 'var(--text-secondary)',
  },
}
